import { Product } from "@/types";
import { Link } from "@inertiajs/react";
import { useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import {
    ArrowLeft,
    ExternalLink,
    Package,
    Truck,
    Clock,
    ChevronLeft,
    ChevronRight,
    Store,
    Palette,
    Info,
} from "lucide-react"

type ProductProps = {
    product: Product;
}

export default function ProductDetailPage({ product }: ProductProps) {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0)

    const nextImage = () => {
        setSelectedImageIndex((prev) => (prev + 1) % product.images.length)
    }

    const prevImage = () => {
        setSelectedImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length)
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("pl-PL", {
            year: "numeric",
            month: "long",
            day: "numeric",
        })
    }

    return (
        <main className="min-h-screen bg-background">
            <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
                {/* Breadcrumb */}
                <Link
                    href="/"
                    className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                    <ArrowLeft className="size-4" />
                    Wróć do listy produktów
                </Link>

                <div className="grid gap-8 lg:grid-cols-2">
                    {/* Image Gallery */}
                    <div className="space-y-4">
                        <div className="relative aspect-square overflow-hidden rounded-lg bg-secondary">
                            <img
                                src={product.images[selectedImageIndex] || "/placeholder.svg"}
                                alt={product.name}
                                className="object-cover"
                            />
                            {product.images.length > 1 && (
                                <>
                                    <button
                                        onClick={prevImage}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-background/80 p-2 transition-colors hover:bg-background"
                                        aria-label="Poprzednie zdjęcie"
                                    >
                                        <ChevronLeft className="size-5" />
                                    </button>
                                    <button
                                        onClick={nextImage}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-background/80 p-2 transition-colors hover:bg-background"
                                        aria-label="Następne zdjęcie"
                                    >
                                        <ChevronRight className="size-5" />
                                    </button>
                                </>
                            )}
                            {product.special_price && (
                                <Badge className="absolute left-3 top-3 bg-destructive text-destructive-foreground">Promocja</Badge>
                            )}
                        </div>

                        {/* Thumbnails */}
                        {product.images.length > 1 && (
                            <div className="flex gap-2 overflow-x-auto pb-2">
                                {product.images.map((image, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedImageIndex(index)}
                                        className={`relative size-20 shrink-0 overflow-hidden rounded-md border-2 transition-colors ${index === selectedImageIndex ? "border-primary" : "border-transparent"
                                            }`}
                                    >
                                        <img src={image || "/placeholder.svg"} alt={`${product.name} - zdjęcie ${index + 1}`} className="object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Product Info */}
                    <div className="space-y-6">
                        {/* Header */}
                        <div>
                            <div className="mb-2 flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                    {product.product_code}
                                </Badge>
                            </div>
                            <h1 className="mb-2 text-2xl font-bold text-foreground sm:text-3xl">{product.name}</h1>
                            <p className="text-lg text-muted-foreground">{product.name}</p>
                        </div>

                        {/* Price */}
                        <div className="space-y-2">
                            <div className="flex items-baseline gap-3">
                                {!!product.special_price ? (
                                    <>
                                        <span className="text-3xl font-bold text-destructive">{product.special_price.toFixed(2)} zł</span>
                                        <span className="text-xl text-muted-foreground line-through">{product.price.toFixed(2)} zł</span>
                                    </>
                                ) : (
                                    <span className="text-3xl font-bold text-foreground">{product.price.toFixed(2)} zł</span>
                                )}
                            </div>
                            {!!product.special_price_conditions && (
                                <p className="text-sm text-muted-foreground">{product.special_price_conditions}</p>
                            )}
                            {!!product.min_price && (
                                <p className="text-sm text-muted-foreground">
                                    Cena od {product.min_price.toFixed(2)} zł przy większych zamówieniach
                                </p>
                            )}
                        </div>

                        {/* Vendor */}
                        <Card>
                            <CardContent className="flex items-center gap-3 p-4">
                                <div className="flex size-10 items-center justify-center rounded-full bg-secondary">
                                    <Store className="size-5 text-muted-foreground" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-muted-foreground">Sprzedawca</p>
                                    <p className="font-medium">{product.vendor_name}</p>
                                </div>
                                <Button asChild>
                                    <a href={product.link} target="_blank" rel="noopener noreferrer" className="gap-2">
                                        Zobacz w sklepie
                                        <ExternalLink className="size-4" />
                                    </a>
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Stock & Supply */}
                        <div className="grid grid-cols-2 gap-4">
                            {product.stock !== null && (
                                <Card>
                                    <CardContent className="flex items-center gap-3 p-4">
                                        <Package className="size-5 text-muted-foreground" />
                                        <div>
                                            <p className="text-sm text-muted-foreground">Na stanie</p>
                                            <p className="font-medium">{product.stock.toLocaleString("pl-PL")} szt.</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                            {product.supply !== null && (
                                <Card>
                                    <CardContent className="flex items-center gap-3 p-4">
                                        <Truck className="size-5 text-muted-foreground" />
                                        <div>
                                            {product.supply && <p className="text-sm text-muted-foreground">Dostawa {product.supply} szt.</p>}
                                            {product.supply_desc && <p className="font-medium">{product.supply_desc}</p>}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        {product.far_stock !== null && product.far_stock_desc && (
                            <p className="text-sm text-muted-foreground">
                                <Info className="mr-1 inline size-4" />
                                {product.far_stock_desc}: {product.far_stock.toLocaleString("pl-PL")} szt.
                            </p>
                        )}

                        {/* Colors */}
                        {product.color && (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                    <Palette className="size-4 text-muted-foreground" />
                                    Dostępne kolory
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {product.color.split(",").map((color) => (
                                        <Badge key={color.trim()} variant="secondary">
                                            {color.trim()}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <Separator className="my-8" />

                {/* Description & Details */}
                <div className="grid gap-8 lg:grid-cols-3">
                    {/* Description */}
                    <div className="lg:col-span-2">
                        <h2 className="mb-4 text-xl font-semibold">Opis produktu</h2>
                        <p className="whitespace-pre-line leading-relaxed text-muted-foreground">{product.description}</p>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Details */}
                        {/* {product.details && Object.keys(product.details).length > 0 && (
                            <Card>
                                <CardContent className="p-4">
                                    <h3 className="mb-4 flex items-center gap-2 font-semibold">
                                        <Tag className="size-4" />
                                        Specyfikacja
                                    </h3>
                                    <dl className="space-y-3">
                                        {Object.entries(product.details).map(([key, value]) => (
                                            <div key={key} className="flex justify-between gap-4 text-sm">
                                                <dt className="text-muted-foreground">{key}</dt>
                                                <dd className="text-right font-medium">{value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </CardContent>
                            </Card>
                        )} */}

                        {/* Characteristics */}
                        {product.characteristics && Object.keys(product.characteristics).length > 0 && (
                            <Card>
                                <CardContent className="p-4">
                                    <h3 className="mb-4 flex items-center gap-2 font-semibold">
                                        <Info className="size-4" />
                                        Cechy produktu
                                    </h3>
                                    <dl className="space-y-3">
                                        {Object.entries(product.characteristics).map(([key, value]) => (
                                            <div key={key} className="flex justify-between gap-4 text-sm">
                                                <dt className="text-muted-foreground">{key}</dt>
                                                <dd className="text-right font-medium">{value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </CardContent>
                            </Card>
                        )}

                        {/* Metadata */}
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Clock className="size-4" />
                                    Aktualizacja: {formatDate(product.updated_at)}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </main>
    )
}
